import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FeedbackFormData } from "../../data/FeedbackFormData";
import { FeedbackItem } from "../../data/FeedbackItem";
import Rating from "@mui/material/Rating";
import { SmileyIconContainer } from "./SmileyIconContainer";
import { useTranslation } from "react-i18next";
import { makeStyles, withStyles } from 'tss-react/mui';
import {
    Button,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField, Theme,
    Typography,
} from "@mui/material";
import {CSSObject} from "tss-react";

const StyledFormControlLabel = withStyles(FormControlLabel, (theme: Theme) => ({
    label: {
        ...theme.typography.caption,
        textAlign: "center"
    } as CSSObject, // TODO: check if works
    root: {
        margin: "0 6px",
    },
}));

const StyledRating = withStyles(Rating, {
    iconEmpty: {
        color: "black",
    },
    iconFilled: {
        color: "#9D1681",
    },
});

const useStyles = makeStyles()({
    root: {
        marginBottom: "30px",
        display: "flex",
        flexFlow: "column nowrap",
    },
    formElement: {
        margin: "15px 0",
    },
    radioGroup: {
        flexFlow: "row nowrap",
    },
    longInput: {
        width: "100%",
    },
    saveButton: {
        paddingLeft: "30px",
        paddingRight: "30px",
        marginBottom: "20px",
        alignSelf: "start",
    },
});

interface FeedbackFormProps {
    onAdd: (newFeedback: FeedbackItem[]) => void;
}

type ObjectEntry = (string | number)[];

export function FeedbackForm(props: FeedbackFormProps) {
    // HACK: objects have unordered entries, so we cannot
    //  rely on their ordering for rendering;
    //  arrays stay ordered
    // HACK: TypeScript doesn't accept a [number, string][] type :/
    const formAnswers: { [k: string]: ObjectEntry[] } = {
        clarity: [
            [5, "awesome"],
            [4, "pretty-good"],
            [3, "ok"],
            [2, "not-so-good"],
            [1, "really-bad"],
        ],
        enoughTime: [
            [5, "awesome"],
            [4, "pretty-good"],
            [3, "ok"],
            [2, "not-so-good"],
            [1, "really-bad"],
        ],
        liveCoding: [
            [5, "awesome"],
            [4, "pretty-good"],
            [3, "ok"],
            [2, "not-so-good"],
            [1, "really-bad"],
            [6, "no-comparison"],
        ],
        generalRating: [
            [1, "really-bad"],
            [2, "ok"],
            [3, "awesome"],
        ],
    };

    const { onAdd } = props;
    const { classes } = useStyles();
    const { t, i18n } = useTranslation("feedbackPage");

    const schema = yup.object().shape({
        openQuestion: yup
            .string()
            .nullable()
            .max(500, "Feedback cannot be longer than 500 characters"),
        clarityRating: yup
            .number()
            .min(0, "Rate cannot be lower than 0!")
            .max(5, "Rate cannot be higher than 5!"),
        timeRating: yup
            .number()
            .min(0, "Rate cannot be lower than 0!")
            .max(5, "Rate cannot be higher than 5!"),
        liveCodingRating: yup
            .number()
            .min(0, "Rate cannot be lower than 0!")
            .max(6, "Rate cannot be higher than 6!"),
        generalTestRating: yup
            .number()
            .min(0, "Rate cannot be lower than 0!")
            .max(3, "Rate cannot be higher than 3!"),
    });

    const {
        handleSubmit,
        formState: { errors, isValid },
        control,
    } = useForm<FeedbackFormData>({
        resolver: yupResolver<any>(schema),
        mode: "all",
        defaultValues: {
            clarityRating: 0,
            timeRating: 0,
            liveCodingRating: 0,
            generalTestRating: 0,
            openQuestion: "",
        },
    });

    const polishT = i18n.getFixedT("pl", "feedbackPage");

    function getPolishLabelForRating(
        questionLabel: string,
        rating: number
    ): string {
        const answers = formAnswers[questionLabel];

        const labelIndex = answers.findIndex(
            ([ratingFromArray]) => rating === ratingFromArray
        );

        if (labelIndex >= 0) {
            const [, label] = answers[labelIndex];
            const fullLabel = `${questionLabel}.answers.${label as string}`;
            return polishT(fullLabel);
        } else {
            console.warn(`no label found for rating ${rating}`);
            return "";
        }
    }

    function submitForm(data: FeedbackFormData) {
        const {
            clarityRating,
            timeRating,
            liveCodingRating,
            generalTestRating,
            openQuestion,
        } = data;

        onAdd([
            getFeedbackItemForQuestion("clarity", clarityRating),
            getFeedbackItemForQuestion("enoughTime", timeRating),
            getFeedbackItemForQuestion("liveCoding", liveCodingRating),
            getFeedbackItemForQuestion("generalRating", generalTestRating),
            getFeedbackItemForQuestion("openQuestion", openQuestion),
        ]);
    }

    function getFeedbackItemForQuestion(
        questionLabel: string,
        answer: any
    ): FeedbackItem {
        if (typeof answer === "string") {
            return getTextFeedbackItemForQuestion(questionLabel, answer);
        } else if (typeof answer === "number") {
            return getNumericFeedbackItemForQuestion(questionLabel, answer);
        } else
            return getTextFeedbackItemForQuestion(
                questionLabel,
                JSON.stringify(answer)
            );
    }

    function getNumericFeedbackItemForQuestion(
        questionLabel: string,
        answer: number
    ): FeedbackItem {
        return {
            question: polishT(`${questionLabel}.question`),
            feedbackType: "NUMERIC",
            answer,
            description: getPolishLabelForRating(
                questionLabel,
                answer as number
            ),
        };
    }

    function getTextFeedbackItemForQuestion(
        questionLabel: string,
        answer: string
    ): FeedbackItem {
        return {
            question: polishT(`${questionLabel}.question`),
            feedbackType: "TEXT",
            answer,
        };
    }

    function formControlLabelsForQuestion(questionLabel: string): any[] {
        const translations = t(`${questionLabel}.answers`, {
            returnObjects: true,
        }) as unknown as { [k: string]: string };

        return formAnswers[questionLabel].map(([rating, answerLabel]) => {
            return (
                <StyledFormControlLabel
                    key={rating.toString()}
                    value={rating.toString()}
                    label={translations[answerLabel]}
                    labelPlacement="bottom"
                    data-cy={translations[answerLabel]}
                    control={<Radio color="primary" />}
                />
            );
        });
    }

    return (
        <form onSubmit={handleSubmit(submitForm)} className={classes.root}>
            <FormControl component="fieldset" className={classes.formElement}>
                <Typography component="legend" variant="h6">
                    {t("clarity.question")}
                </Typography>
                <Controller
                    name="clarityRating"
                    control={control}
                    render={(props) =>
                        <RadioGroup
                            row
                            aria-label="position"
                            name="position"
                            defaultValue="top"
                            data-cy="clarity"
                            className={classes.radioGroup}
                        >
                            {formControlLabelsForQuestion("clarity")}
                        </RadioGroup>
                    }
                />
            </FormControl>
            <FormControl component="fieldset" className={classes.formElement}>
                <Typography component="legend" variant="h6">
                    {t("enoughTime.question")}
                </Typography>
                <Controller
                    name="timeRating"
                    control={control}
                    render={(props) =>
                        <RadioGroup
                            row
                            aria-label="position"
                            name="position"
                            defaultValue="top"
                            data-cy="enoughTime"
                            className={classes.radioGroup}
                        >
                            {formControlLabelsForQuestion("enoughTime")}
                        </RadioGroup>
                    }
                />
            </FormControl>
            <FormControl component="fieldset" className={classes.formElement}>
                <Typography component="legend" variant="h6">
                    {t("liveCoding.question")}
                </Typography>
                <Controller
                    name="liveCodingRating"
                    control={control}
                    render={(props) =>
                        <RadioGroup
                            row
                            aria-label="position"
                            name="position"
                            defaultValue="top"
                            data-cy="liveCoding"
                            className={classes.radioGroup}
                        >
                            {formControlLabelsForQuestion("liveCoding")}
                        </RadioGroup>
                    }
                />
            </FormControl>
            <FormControl component="fieldset" className={classes.formElement}>
                <Typography component="legend" variant="h6">
                    {t("generalRating.question")}
                </Typography>
                <Controller
                    name="generalTestRating"
                    control={control}
                    render={(props) =>
                        <StyledRating
                            size="large"
                            name="testRate"
                            data-cy="generalTestRating"
                            max={3}
                            IconContainerComponent={SmileyIconContainer}
                            highlightSelectedOnly
                        />
                    }
                />
            </FormControl>

            <div className={classes.formElement}>
                <Typography variant="h6">
                    {t("openQuestion.question")}
                </Typography>
                <Controller
                    name="openQuestion"
                    control={control}
                    render={(props) => (
                        <TextField
                            {...props.field}
                            label={t("openQuestion.label")}
                            data-cy="openQuestion"
                            error={errors.openQuestion !== undefined}
                            helperText={errors.openQuestion?.message}
                            className={classes.longInput}
                        />
                    )}
                />
            </div>

            <Button
                disabled={!isValid}
                color="secondary"
                variant="contained"
                className={classes.saveButton}
                type="submit"
            >
                {t("endAssignment")}
            </Button>
        </form>
    );
}
